import type { FC } from "react";
import { AppImage } from "@components/NextImage";
import { cn } from "utils";
import { IntroHeadline } from "./IntroHeadline";
import { IntroSegmentProgress } from "./IntroSegmentProgress";
import type { IntroSlideShellProps } from "./types";
import { INTRO_SLIDE_THEMES, SIGN_IN_INTRO_ASSETS } from "./introSlidesConfig";
import { useIntroActiveIndex } from "./IntroActiveIndexContext";
import {
  INTRO_DESKTOP_CONTENT_PANEL_CLASS,
  IntroDesktopHeadlineStack,
  IntroDesktopVisualPanel,
} from "./IntroSlideShellDesktop";

/** Figma progress / branding track — 343px (H5 96293:45200 · PC 96344:109892) */
const INTRO_BRANDING_TRACK_CLASS = "w-full max-w-[343px] self-start";

export const IntroSlideShell: FC<Omit<IntroSlideShellProps, "activeIndex">> = ({
  slideIndex,
  headline,
  subtitle,
  children,
  desktopVisual,
  hideDesktopHero = false,
  className,
}) => {
  const activeIndex = useIntroActiveIndex();
  const theme = INTRO_SLIDE_THEMES[slideIndex] ?? INTRO_SLIDE_THEMES[0];
  const scrimLayers =
    theme.scrimLayers && theme.scrimLayers.length > 0
      ? theme.scrimLayers
      : theme.scrimClassName
        ? [theme.scrimClassName]
        : [];

  const headlineNode =
    typeof headline === "string" ? <IntroHeadline text={headline} /> : headline;

  const brandLogo = (
    <AppImage
      src={SIGN_IN_INTRO_ASSETS.brandLogo}
      alt="ed"
      width={60}
      height={32}
      className="block h-8 w-[60px] shrink-0 object-left object-contain"
      data-testid="intro-brand-logo"
      data-node-id="96293:46165"
    />
  );

  return (
    <div
      className={cn(
        "relative flex h-full min-h-dvh w-screen max-w-full flex-shrink-0 overflow-hidden",
        "flex-col md:flex-row",
        theme.fallbackClassName,
        theme.desktopShellClassName,
        className,
      )}
      data-testid={`intro-slide-shell-${slideIndex}`}
      data-layout="intro-slide-responsive"
    >
      {/* H5: full-bleed background + scrims */}
      {theme.backgroundImage ? (
        <div className="pointer-events-none absolute inset-0 overflow-hidden md:hidden">
          <div
            className={cn(
              theme.backgroundImageWrapperClassName ?? "absolute inset-0",
            )}
          >
            <AppImage
              src={theme.backgroundImage}
              alt=""
              width={393}
              height={852}
              loading={slideIndex === 0 ? "eager" : "lazy"}
              className={cn(
                "pointer-events-none absolute inset-0 h-full w-full",
                theme.backgroundImageClassName ?? "object-cover object-center",
              )}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
        </div>
      ) : null}
      {scrimLayers.map((layerClassName, index) => (
        <div
          key={`${slideIndex}-scrim-${index}`}
          className={cn(
            "pointer-events-none absolute inset-0 md:hidden",
            layerClassName,
          )}
          aria-hidden
        />
      ))}

      {/* Left / full-width content panel */}
      <div
        className={cn(INTRO_DESKTOP_CONTENT_PANEL_CLASS, "md:bg-inherit")}
        data-testid="intro-content-panel"
      >
        <div className="flex min-h-0 flex-1 flex-col md:gap-[132px]">
          {/* H5 — progress → logo + headline (left edges share 343px track) */}
          <div className="flex flex-col items-start md:hidden">
            <IntroSegmentProgress
              activeIndex={activeIndex}
              className={cn("mt-3 shrink-0", INTRO_BRANDING_TRACK_CLASS)}
            />
            <div
              className={cn(
                "mt-8 flex flex-col gap-6",
                INTRO_BRANDING_TRACK_CLASS,
              )}
              data-testid="intro-top-column"
              data-node-id="96293:46163"
            >
              {brandLogo}
              <IntroDesktopHeadlineStack
                headline={headlineNode}
                subtitle={subtitle}
              />
            </div>
          </div>

          {/* PC — logo + progress (96344:110638 · left-aligned to track) */}
          <div
            className={cn(
              "hidden flex-col items-start gap-5 md:flex",
              INTRO_BRANDING_TRACK_CLASS,
            )}
            data-testid="intro-branding-block"
            data-node-id="96344:110638"
          >
            {brandLogo}
            <IntroSegmentProgress
              activeIndex={activeIndex}
              className="w-full"
            />
          </div>

          {/* PC — headline + hero block (96344:110652) */}
          <div
            className="hidden flex-col gap-12 md:flex"
            data-testid="intro-desktop-content-block"
            data-node-id="96344:110652"
          >
            <IntroDesktopHeadlineStack
              headline={headlineNode}
              subtitle={subtitle}
            />
            {!hideDesktopHero ? (
              <div data-testid="intro-hero-slot">{children}</div>
            ) : null}
          </div>

          {/* H5 — Ed hero bubble (~319px from frame top in Figma) */}
          <div
            className="mt-[100px] flex flex-col md:hidden"
            data-testid="intro-hero-slot"
          >
            {children}
          </div>
        </div>
      </div>

      <IntroDesktopVisualPanel
        slideIndex={slideIndex}
        theme={theme}
        overlay={desktopVisual}
      />
    </div>
  );
};

interface IntroGetStartedCtaProps {
  label: string;
  onClick: () => void;
}

export const IntroGetStartedCta: FC<IntroGetStartedCtaProps> = ({
  label,
  onClick,
}) => (
  <div className="absolute bottom-0 left-0 z-20 mb-4 w-full px-[26px] pb-safe md:bottom-16 md:left-[72px] md:mb-0 md:w-auto md:max-w-none md:px-0 md:pb-0">
    <button
      type="button"
      data-testid="intro-get-started-cta"
      data-node-id="96293:46176"
      data-openreplay-label="misc_signinintrogetstarted_click"
      onClick={onClick}
      className={cn(
        // Figma Button / General — H5 96293:46176 · PC 96344:110674
        // bg: background/solid/neutral/primary · text: content/primary · button_l
        "button flex items-center justify-center rounded-lg shrink-0",
        "bg-background_solid_neutral_primary text-content_primary",
        "button_m h-12 w-full max-w-[345px] mx-auto px-4",
        "md:button_l md:h-14 md:w-auto md:mx-0 md:min-w-[80px] md:px-10",
      )}
    >
      {label}
    </button>
  </div>
);
